import { aggregateQuantityGroups, singleQuantityGroup } from "../../../shared/production/quantity";
import { downtimeReportSummary, isShortProductionDowntime } from "../../../shared/production/downtime";
import type { LineReportItem, ProductionRun } from "./models";

export function operatorLineSummaries(runs: readonly ProductionRun[], now = Date.now()): LineReportItem[] {
  const byLine = new Map<string, ProductionRun[]>();
  for (const run of runs) byLine.set(run.lineId, [...(byLine.get(run.lineId) ?? []), run]);
  return [...byLine].map(([lineId, members]) => {
    const quantityGroups = aggregateQuantityGroups(members), totals = singleQuantityGroup(quantityGroups);
    const stops = members.map((run) => downtimeReportSummary(run.downtimes, now, run.reportWindow));
    const scrapBreakdown = new Map<string, LineReportItem["scrapBreakdown"][number]>();
    for (const run of members) for (const entry of run.entries) {
      if (entry.voidedAt || !["SCRAP", "REWORK"].includes(entry.type)) continue;
      const code = entry.destinationStockCode ?? "PENDING_CODING", key = JSON.stringify([code, entry.unit]);
      const previous = scrapBreakdown.get(key) ?? { code, name: entry.destinationStockName ?? "Kodlama bekliyor", unit: entry.unit, quantity: 0 };
      previous.quantity += entry.quantity; scrapBreakdown.set(key, previous);
    }
    return { lineId, lineName: members[0].lineName, production: totals.production, waste: totals.waste, scrap: totals.scrap,
      quantityGroups, unit: totals.unit, unknownUnitEntryCount: totals.unknownCount, runCount: members.length,
      downtimeMinutes: stops.reduce((sum, item) => sum + item.totalMilliseconds, 0) / 60_000,
      shortStopCount: stops.reduce((sum, item) => sum + item.shortStopCount, 0),
      shortStopSeconds: stops.reduce((sum, item) => sum + item.shortStopMilliseconds, 0) / 1000,
      scrapBreakdown: [...scrapBreakdown.values()] };
  }).sort((a, b) => a.lineName.localeCompare(b.lineName, "tr-TR"));
}

export function operatorDowntimeGroups(runs: readonly ProductionRun[], now = Date.now()) {
  const groups = new Map<string, { name: string; count: number; milliseconds: number }>();
  for (const run of runs) for (const event of run.downtimes) {
    const summary = downtimeReportSummary([event], now, run.reportWindow);
    if (summary.invalidIntervalCount || (summary.totalMilliseconds === 0 && summary.shortStopCount === 0)) continue;
    const name = isShortProductionDowntime(event) ? "Kısa duruşlar" : event.reasonName ?? (event.endedAt ? "Nedeni girilmemiş duruş" : "Devam eden duruş");
    const current = groups.get(name) ?? { name, count: 0, milliseconds: 0 };
    current.count++; current.milliseconds += summary.totalMilliseconds; groups.set(name, current);
  }
  return [...groups.values()].sort((a, b) => b.milliseconds - a.milliseconds);
}
